import {
    Component,
    computed,
    effect,
    EventEmitter,
    inject,
    Input,
    Output,
    signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Button } from 'primeng/button';
import { Dialog } from 'primeng/dialog';
import { IconField } from 'primeng/iconfield';
import { InputIcon } from 'primeng/inputicon';
import { InputText } from 'primeng/inputtext';
import { Message } from 'primeng/message';
import { TableModule } from 'primeng/table';
import { Tag } from 'primeng/tag';
import { Tooltip } from 'primeng/tooltip';
import { AdminGame, GameVariant, GameVariantTemplate } from '@/lib-backend';
import { RealmGameVariantsService } from '@/app/services/realm-game-variants.service';
import {
    effectiveVariantDisplayInfo,
    effectiveVariantIsActive,
    modeConfigTypeLabel,
    variantEntryFee,
    variantPlayerCount,
    variantTopReward,
} from '@/app/utils/variant';
import { effectiveTitle } from '@/app/utils/game';
import { FundsDisplay } from '@/app/components/funds-display';
import { AddVariantDialog } from './add-variant-dialog';
import { ConfigureVariantDialog } from './configure-variant-dialog';
import { EditVariantDialog } from './edit-variant-dialog';

@Component({
    selector: 'app-variants-dialog',
    standalone: true,
    imports: [
        FormsModule, Button, Dialog, IconField, InputIcon, InputText, Message, TableModule, Tag, Tooltip, FundsDisplay,
        AddVariantDialog, ConfigureVariantDialog, EditVariantDialog,
    ],
    templateUrl: './variants-dialog.html',
})
export class VariantsDialog {
    @Input({ required: true }) set visible(v: boolean) {
        this.visibleSignal.set(v);
    }

    @Input() set adminGame(g: AdminGame | null) {
        this.gameSignal.set(g);
    }

    @Output() visibleChange = new EventEmitter<boolean>();

    protected readonly variantsService = inject(RealmGameVariantsService);

    protected readonly visibleSignal = signal(false);
    protected readonly gameSignal = signal<AdminGame | null>(null);

    protected readonly addDialogVisible = signal(false);
    protected readonly configureDialogVisible = signal(false);
    protected readonly selectedTemplate = signal<GameVariantTemplate | null>(null);
    protected readonly editDialogVisible = signal(false);
    protected readonly editingVariant = signal<GameVariant | null>(null);

    protected readonly searchTerm = signal('');
    protected readonly filteredVariants = computed(() => {
        const term = this.searchTerm().trim().toLowerCase();
        if (!term) return this.variantsService.variants();
        return this.variantsService.variants().filter((v) => {
            const di = effectiveVariantDisplayInfo(v);
            const name = di.Name.toLowerCase();
            const desc = (di.Description ?? '').toLowerCase();
            const mode = modeConfigTypeLabel(v.Template.ModeConfig).toLowerCase();
            return name.includes(term) || desc.includes(term) || mode.includes(term);
        });
    });

    protected readonly effectiveTitle = effectiveTitle;
    protected readonly effectiveVariantDisplayInfo = effectiveVariantDisplayInfo;
    protected readonly effectiveVariantIsActive = effectiveVariantIsActive;
    protected readonly modeConfigTypeLabel = modeConfigTypeLabel;
    protected readonly variantPlayerCount = variantPlayerCount;
    protected readonly variantEntryFee = variantEntryFee;
    protected readonly variantTopReward = variantTopReward;

    constructor() {
        effect(() => {
            const visible = this.visibleSignal();
            const g = this.gameSignal();
            if (visible && g) {
                this.variantsService.loadVariants(g.RealmId, g.Id);
            }
        });
    }

    protected onVisibleChange(value: boolean): void {
        this.visibleSignal.set(value);
        this.visibleChange.emit(value);
        if (!value) {
            this.variantsService.clear();
        }
    }

    protected close(): void {
        this.onVisibleChange(false);
    }

    protected refresh(): void {
        const g = this.gameSignal();
        if (g) {
            this.variantsService.loadVariants(g.RealmId, g.Id);
        }
    }

    protected openAdd(): void {
        this.addDialogVisible.set(true);
    }

    protected onTemplateSelected(t: GameVariantTemplate): void {
        this.selectedTemplate.set(t);
        this.configureDialogVisible.set(true);
    }

    protected onVariantCreated(): void {
        this.refresh();
    }

    protected openEdit(v: GameVariant): void {
        this.editingVariant.set(v);
        this.editDialogVisible.set(true);
    }

    protected trackById(_index: number, v: GameVariant): string {
        return v.GameVariantId;
    }
}
